import { computed, Injectable, signal } from '@angular/core';

/** Which edge of the viewport the panel docks to (`start` = left, `end` = right). */
export type QaPanelSide = 'start' | 'end';

/**
 * How the panel is presented: docked to the left edge, docked to the right edge, or detached into a
 * separate browser window.
 */
export type QaPanelMode = 'dock-start' | 'dock-end' | 'window';

/**
 * UI-state service for the QA Controls panel.
 *
 * Decouples the toggle trigger (header button) from the panel host (layout) so neither needs a
 * direct reference to the other. Holds only open/closed and presentation-mode state -- no routing,
 * no group logic. The external-window mechanics live in `QaWindowService`; this service just owns
 * the `mode` signal that everything else reacts to.
 */
@Injectable({ providedIn: 'root' })
export class QaPanelService {
    readonly isOpen = signal(false);

    /** Presentation mode; defaults to docked on the left edge. */
    readonly mode = signal<QaPanelMode>('dock-start');

    /** True while the panel is docked (either edge); false while it lives in a separate window. */
    readonly isDocked = computed(() => this.mode() !== 'window');

    /**
     * Dock side for the offcanvas. Only meaningful while docked; resolves to `start` when windowed
     * so the offcanvas placeholder still has a defined edge. Defaults to the left edge.
     */
    readonly side = computed<QaPanelSide>(() => (this.mode() === 'dock-end' ? 'end' : 'start'));

    open(): void {
        this.isOpen.set(true);
    }

    close(): void {
        this.isOpen.set(false);
    }

    toggle(): void {
        this.isOpen.update((open) => !open);
    }

    dockStart(): void {
        this.mode.set('dock-start');
    }

    dockEnd(): void {
        this.mode.set('dock-end');
    }

    popOut(): void {
        this.mode.set('window');
    }
}
